import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html, Line } from '@react-three/drei';
import * as THREE from 'three';


import { MeasurementScale } from './MeasurementScale.js'

// WhiteGreenDot component
const WhiteGreenDot = ({ size = "12px", dotColor = "#00ff00" }) => {
  return (
    <div
      style={{
        display: 'block',
        position: 'relative',
        width: size,
        height: size,
        backgroundColor: 'white',
        borderRadius: '50%'
      }}
    >
      <div
        style={{
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '66%',
          height: '66%',
          backgroundColor: dotColor,
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      />
    </div>
  );
};

// ModelText component
const ModelText = ({ text, variant = "right", dotColor = "#00ff00", dotSize = "12px" }) => {
  const isLeft = variant === 'left';

  const formatText = (input) => {
    if (typeof input !== 'string') return input;
    return input.split(/(\d+)/).map((part, i) =>
      /\d+/.test(part) ? <sub key={i} style={{ fontSize: '0.6em', verticalAlign: 'sub' }}>{part}</sub> : part
    );
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: isLeft ? 'row-reverse' : 'row',
      alignItems: 'center',
      width: 'max-content',
      transform: isLeft ? 'translateX(-100%)' : 'none'
    }}>
      <div
        style={{
          borderRadius: '50%',
          width: dotSize,
          height: dotSize,
          backgroundColor: dotColor,
          flexShrink: 0
        }}
      />
      <div
        className="model-text"
        style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#425a70',
          whiteSpace: 'nowrap',
          paddingLeft: isLeft ? '0' : '10px',
          paddingRight: isLeft ? '10px' : '0'
        }}
      >
        {formatText(text)}
      </div>
    </div>
  );
};

export const Wires = ({
  core,
  coreModification,
  coreColor,
  surfaceFunctionalization,
  surfaceFunctionalizationType = 'No modification',
  surfaceFunctionalizationColor,
  lineColor = "#00ff00",
  baseRotation = [Math.PI / 18, Math.PI / 4, Math.PI / 2],
  isNanoformImageGenerated = false,
  measureScale = 0
}) => {
  const modelGroup = useRef();
  const y = baseRotation[1];

  // Wire positions
  const positions = useMemo(
    () => [
      [0, 0.2, -0.4],
      [0, 0, 0],
      [0, -0.2, 0.4],
    ],
    []
  );

  useFrame(() => {
    if (modelGroup.current) {
      if (isNanoformImageGenerated) {
        modelGroup.current.rotation.y = y;
      } else {
        modelGroup.current.rotation.y += 0.005;
      }
    }
  });

  // Create cylinder geometries for wires
  const coreWireGeometry = new THREE.CylinderGeometry(0.12, 0.12, 6, 30);
  const haloWireGeometry = new THREE.CylinderGeometry(0.17, 0.17, 5.99, 30);

  return (
    <group position={[0, -0.7, 0]}>
      {/* Core label */}
      {Boolean(core) && (
        <group>
          <Html className="core" position={[1, 1, 2]} zIndexRange={[0, 1]}>
            <ModelText
              dotSize="12px"
              text={coreModification ? `${core?.name}/${coreModification}` : (core?.name || 'Core')}
              variant="right"
              dotColor={lineColor}
            />
          </Html>
          <Html className="center" position={[0, 0.3, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="12px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [0, 0.3, 2],
              [0.7, 1, 2],
              [1, 1, 2],
            ]}
            lineWidth={3}
            color={lineColor}
          />
        </group>
      )}

      {/* Surface functionalization label */}
      {Boolean(surfaceFunctionalization) && (
        <group>
          <Html className="core--left" position={[-0.8, 0.89, 2]} zIndexRange={[0, 1]}>
            <ModelText
              dotSize="12px"
              text={surfaceFunctionalizationType}
              variant="left"
              dotColor={lineColor}
            />
          </Html>
          <Html className="center" position={[-0.1, 0.38, 2]} zIndexRange={[0, 1]}>
            <WhiteGreenDot size="12px" dotColor={lineColor} />
          </Html>
          <Line
            points={[
              [-0.1, 0.38, 2],
              [-0.5, 0.85, 2],
              [-0.8, 0.85, 2],
            ]}
            lineWidth={1.5}
            color={lineColor}
          />
        </group>
      )}
      <MeasurementScale width={measureScale} unit="nm" color="#425a70" />

      {/* 3D Model - Multiple Wires */}
      <group ref={modelGroup} rotation={baseRotation}>
        {positions.map((position, index) => (
          <group key={`wire-${index}`}>
            {/* Core wire */}
            <mesh position={position} geometry={coreWireGeometry}>
              <meshStandardMaterial
                attach="material"
                roughness={0}
                metalness={0.1}
                color={coreColor}
              />
            </mesh>

            {/* Halo wire */}
            {Boolean(surfaceFunctionalization) && (
              <mesh position={position} geometry={haloWireGeometry}>
                <meshStandardMaterial
                  transparent
                  opacity={0.35}
                  color={surfaceFunctionalizationColor}
                />
              </mesh>
            )}
          </group>
        ))}
      </group>
    </group>
  );
};